import { useEffect, useState } from "react";
import { getCoreRowModel, useReactTable } from "@tanstack/react-table";
import type { SortingState } from "@tanstack/react-table";
import { useNavigate } from "react-router-dom";

import type { PartnerListFilters } from "@/api/partners";
import { usePartners } from "@/hooks";
import { usePagination } from "@/hooks/usePagination";
import type { Partner } from "@/types";
import { Pagination, Table } from "@/ui";
import { getPartnersTableColumns } from "./PartnersTableColumns";

export const PartnersTable = ({
  searchQuery,
  filters,
}: {
  searchQuery: string;
  filters?: Omit<PartnerListFilters, "searchQuery">;
}) => {
  const navigate = useNavigate();
  const { pagination, setPageIndex, onPaginationChange } = usePagination();
  const [sorting, setSorting] = useState<SortingState>([]);
  const { useAllPartners } = usePartners();

  const firstSort = sorting[0];
  const sortParam = firstSort
    ? firstSort.desc
      ? `-${firstSort.id}`
      : firstSort.id
    : undefined;

  const { data: partnersData, isLoading } = useAllPartners(
    pagination.pageIndex + 1,
    { searchQuery, ...filters },
    sortParam,
  );

  const filtersString = JSON.stringify(filters);

  useEffect(() => {
    setPageIndex(0);
  }, [searchQuery, filtersString, sorting, setPageIndex]);

  const table = useReactTable({
    data: partnersData?.data ?? [],
    columns: getPartnersTableColumns(),
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
    manualSorting: true,
    pageCount: partnersData?.pagination?.totalPages ?? 1,
    state: {
      pagination: {
        pageIndex: pagination.pageIndex,
        pageSize: partnersData?.pagination?.perPage ?? 10,
      },
      sorting,
    },
    onPaginationChange,
    onSortingChange: setSorting,
  });

  const handleRowClick = (row: Partner) => {
    navigate(`/partners/${row.id}`);
  };

  return (
    <div>
      <Table
        table={table}
        handleRowClick={handleRowClick}
        isLoading={isLoading}
      />
      <div className="flex items-center justify-between gap-3 rounded-b-2xl border border-neutral-200 px-4">
        <span className="text-sm text-neutral-500">
          {partnersData?.pagination?.total ?? 0}{" "}
          {partnersData?.pagination?.total === 1 ? "partner" : "partners"}
        </span>
        <Pagination
          currentPage={pagination.pageIndex + 1}
          totalPages={partnersData?.pagination?.totalPages ?? 1}
          onPageChange={(page) => setPageIndex(page - 1)}
        />
      </div>
    </div>
  );
};
